import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import { DiningTable, QRTemplate } from '../types';
import QRCode from 'qrcode';
import {
  QrCode,
  Printer,
  Download,
  Flame,
  Palette,
  Wifi,
  Sparkles,
  Layers,
  Plus,
  Trash2
} from 'lucide-react';

export const QRStudio: React.FC = () => {
  const {
    diningTables,
    qrTemplate,
    saveQRTemplate,
    saveDiningTables,
    restaurantProfile
  } = useApp();

  const { t } = useLanguage();

  const [selectedTableNumber, setSelectedTableNumber] = useState<number>(1);
  const [template, setTemplate] = useState<QRTemplate>(qrTemplate);
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [allQrUrls, setAllQrUrls] = useState<Record<number, string>>({});
  const [isPrintMode, setIsPrintMode] = useState(false);

  // Sync template
  useEffect(() => {
    setTemplate(qrTemplate);
  }, [qrTemplate]);

  // Generate QR Data URL for single selected table (Points directly to dedicated /order page)
  useEffect(() => {
    const origin = window.location.origin;
    const url = `${origin}/order?table=${selectedTableNumber}`;

    QRCode.toDataURL(url, {
      width: 400,
      margin: 2,
      color: {
        dark: template.primary_color || '#E05A36',
        light: template.background_color || '#FFFFFF'
      }
    })
      .then(data => setQrDataUrl(data))
      .catch(err => console.error(err));
  }, [selectedTableNumber, template]);

  // Generate all QRs for print sheet (Dedicated /order page)
  useEffect(() => {
    const origin = window.location.origin;
    const generateAll = async () => {
      const map: Record<number, string> = {};
      for (const tbl of diningTables) {
        const url = `${origin}/order?table=${tbl.table_number}`;
        try {
          const data = await QRCode.toDataURL(url, {
            width: 300,
            margin: 2,
            color: {
              dark: template.primary_color || '#E05A36',
              light: template.background_color || '#FFFFFF'
            }
          });
          map[tbl.table_number] = data;
        } catch (e) {
          console.error(e);
        }
      }
      setAllQrUrls(map);
    };
    generateAll();
  }, [diningTables, template]);

  const handleDownloadPNG = () => {
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `TheOvenStory_Table_${selectedTableNumber}_QR.png`;
    a.click();
  };

  const handleSaveTemplate = async () => {
    await saveQRTemplate(template);
    alert('QR-pohja tallennettu onnistuneesti!');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Studio Header */}
      <div
        className="no-print"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          background: 'var(--bg-secondary)',
          padding: '18px 24px',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-subtle)'
        }}
      >
        <div>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.35rem', fontWeight: 800 }}>
            {t('qrStudioTitle')}
          </h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            {t('qrStudioSub')}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn-secondary" onClick={handleDownloadPNG}>
            <Download size={16} />
            <span>{t('downloadQr')}</span>
          </button>
          <button className="btn-primary" onClick={handlePrint}>
            <Printer size={16} />
            <span>{t('printAllSheets')}</span>
          </button>
        </div>
      </div>

      {/* Grid: Live Preview Card on Left & Customizer on Right */}
      <div
        className="no-print"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(340px, 420px) 1fr',
          gap: '24px',
          alignItems: 'flex-start'
        }}
      >
        {/* Table Standee Live Preview */}
        <div
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-medium)',
            borderRadius: 'var(--radius-xl)',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            boxShadow: 'var(--shadow-lg)'
          }}
        >
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-muted)' }}>
            Pöytäkortin Esikatselu (Pöytä #{selectedTableNumber})
          </div>

          {/* Standee Mock Card */}
          <div
            style={{
              width: '100%',
              maxWidth: '320px',
              background: '#FFFFFF',
              color: '#1A1D21',
              borderRadius: '20px',
              padding: '28px 24px',
              boxShadow: '0 12px 36px rgba(0,0,0,0.25)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              border: `3px solid ${template.primary_color}`
            }}
          >
            {/* Brand Title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <Flame size={20} color={template.primary_color} />
              <span
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1.25rem',
                  fontWeight: 800,
                  color: '#1A1D21',
                  letterSpacing: '-0.02em'
                }}
              >
                {template.brand_title || 'The Oven Story'}
              </span>
            </div>

            {/* Tagline */}
            <div style={{ fontSize: '0.8rem', color: '#64748B', fontWeight: 600, marginBottom: '16px' }}>
              {template.tagline_fi || 'Skannaa & Tilaa Pöytääsi'}
            </div>

            {/* QR Code Container */}
            <div
              style={{
                padding: '12px',
                background: '#FFFFFF',
                borderRadius: '16px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                marginBottom: '14px'
              }}
            >
              {qrDataUrl && (
                <img
                  src={qrDataUrl}
                  alt={`QR Pöytä ${selectedTableNumber}`}
                  style={{ width: '180px', height: '180px', display: 'block' }}
                />
              )}
            </div>

            {/* Table Number Badge */}
            <div
              style={{
                background: template.primary_color,
                color: '#FFFFFF',
                padding: '6px 20px',
                borderRadius: '999px',
                fontWeight: 800,
                fontSize: '1.05rem',
                letterSpacing: '0.02em',
                marginBottom: '12px'
              }}
            >
              PÖYTÄ {selectedTableNumber}
            </div>

            {/* WiFi Details if enabled */}
            {template.include_wifi && template.wifi_name && (
              <div
                style={{
                  fontSize: '0.72rem',
                  background: '#F1F5F9',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  color: '#334155',
                  marginBottom: '10px',
                  width: '100%'
                }}
              >
                📶 <strong>WiFi:</strong> {template.wifi_name} | <strong>Salasana:</strong> {template.wifi_password}
              </div>
            )}

            {/* Footer Text */}
            <div style={{ fontSize: '0.72rem', color: '#94A3B8', fontWeight: 600 }}>
              {template.footer_text || 'theovenstory.fi • Helsinki'}
            </div>
          </div>
        </div>

        {/* Customization Controls */}
        <div
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-xl)',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px'
          }}
        >
          <h3 style={{ fontSize: '1.15rem', fontWeight: 800 }}>Pöytäkortin Mukauttaminen</h3>

          {/* Table Selector */}
          <div className="form-group">
            <label className="form-label">Valitse Esikatseltava Pöytä</label>
            <select
              className="form-input"
              value={selectedTableNumber}
              onChange={e => setSelectedTableNumber(Number(e.target.value))}
            >
              {diningTables.map(tbl => (
                <option key={tbl.id} value={tbl.table_number}>
                  {tbl.label} (#{tbl.table_number})
                </option>
              ))}
            </select>
          </div>

          {/* Brand Title */}
          <div className="form-group">
            <label className="form-label">Ravintolan Nimi Pöytäkortissa</label>
            <input
              type="text"
              className="form-input"
              value={template.brand_title}
              onChange={e => setTemplate({ ...template, brand_title: e.target.value })}
            />
          </div>

          {/* Taglines */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Otsikkoteksti (Suomi)</label>
              <input
                type="text"
                className="form-input"
                value={template.tagline_fi}
                onChange={e => setTemplate({ ...template, tagline_fi: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Otsikkoteksti (English)</label>
              <input
                type="text"
                className="form-input"
                value={template.tagline_en}
                onChange={e => setTemplate({ ...template, tagline_en: e.target.value })}
              />
            </div>
          </div>

          {/* Primary Color */}
          <div className="form-group">
            <label className="form-label">Brändiväri (Tehosteväri & QR)</label>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <input
                type="color"
                value={template.primary_color}
                onChange={e => setTemplate({ ...template, primary_color: e.target.value })}
                style={{ width: '44px', height: '44px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
              />
              <input
                type="text"
                className="form-input"
                value={template.primary_color}
                onChange={e => setTemplate({ ...template, primary_color: e.target.value })}
                style={{ maxWidth: '140px' }}
              />
            </div>
          </div>

          {/* WiFi Details */}
          <div
            style={{
              background: 'var(--bg-card)',
              padding: '16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-subtle)',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 700 }}>
              <input
                type="checkbox"
                checked={template.include_wifi}
                onChange={e => setTemplate({ ...template, include_wifi: e.target.checked })}
                style={{ accentColor: 'var(--accent-copper)' }}
              />
              <span>Näytä Asiakas-WiFi Pöytäkortissa</span>
            </label>

            {template.include_wifi && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">WiFi Nimi (SSID)</label>
                  <input
                    type="text"
                    className="form-input"
                    value={template.wifi_name || ''}
                    onChange={e => setTemplate({ ...template, wifi_name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">WiFi Salasana</label>
                  <input
                    type="text"
                    className="form-input"
                    value={template.wifi_password || ''}
                    onChange={e => setTemplate({ ...template, wifi_password: e.target.value })}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer Text */}
          <div className="form-group">
            <label className="form-label">Alatunnisteteksti</label>
            <input
              type="text"
              className="form-input"
              value={template.footer_text}
              onChange={e => setTemplate({ ...template, footer_text: e.target.value })}
            />
          </div>

          <button className="btn-primary" onClick={handleSaveTemplate} style={{ alignSelf: 'flex-start' }}>
            <Sparkles size={16} />
            <span>Tallenna QR-asetukset</span>
          </button>
        </div>
      </div>

      {/* 2. Table Capacity & Seating Layout Studio */}
      <div
        style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-xl)',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '18px'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>🪑</span>
              <span>Pöytäpaikat & Istumapaikkojen Hallinta (Table Capacities)</span>
            </h3>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '3px' }}>
              Määritä ravintolan pöytien istumapaikkamäärät ja salialueet. Asiakkaat näkevät pöytänsä kapasiteetin ja ilmoittavat seurueen koon.
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              className="btn-secondary"
              style={{ padding: '8px 14px', fontSize: '0.86rem', gap: '6px' }}
              onClick={() => {
                const nextNum = diningTables.length > 0 ? Math.max(...diningTables.map(t => t.table_number)) + 1 : 1;
                const newTbl: DiningTable = {
                  id: `tbl_${nextNum}`,
                  table_number: nextNum,
                  label: `Pöytä ${nextNum}`,
                  capacity: 4,
                  zone: 'Pääsali',
                  is_active: true
                };
                saveDiningTables([...diningTables, newTbl]);
              }}
            >
              <Plus size={16} />
              <span>Lisää Uusi Pöytä</span>
            </button>
          </div>
        </div>

        {/* Tables Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '14px'
          }}
        >
          {diningTables.map((tbl, idx) => (
            <div
              key={tbl.id}
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-lg)',
                padding: '16px 18px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                boxShadow: 'var(--shadow-sm)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="table-badge-large">Pöytä #{tbl.table_number}</span>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    ID: {tbl.id}
                  </span>
                </div>

                <button
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--accent-crimson)',
                    cursor: 'pointer',
                    padding: '4px'
                  }}
                  title="Poista pöytä"
                  onClick={() => {
                    if (diningTables.length <= 1) {
                      alert('Ravintolassa on oltava vähintään 1 pöytä.');
                      return;
                    }
                    if (confirm(`Haluatko varmasti poistaa pöydän ${tbl.table_number}?`)) {
                      saveDiningTables(diningTables.filter((_, i) => i !== idx));
                    }
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>

              {/* Table Name Label */}
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontSize: '0.78rem' }}>Pöydän Nimi / Nimike</label>
                <input
                  type="text"
                  className="form-input"
                  value={tbl.label}
                  style={{ padding: '6px 10px', fontSize: '0.86rem' }}
                  onChange={e => {
                    const updated = [...diningTables];
                    updated[idx] = { ...updated[idx], label: e.target.value };
                    saveDiningTables(updated);
                  }}
                />
              </div>

              {/* Capacity & Zone in 2 Columns */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.78rem' }}>🪑 Istumapaikat</label>
                  <select
                    className="form-input"
                    value={tbl.capacity || 4}
                    style={{ padding: '6px 10px', fontSize: '0.86rem' }}
                    onChange={e => {
                      const updated = [...diningTables];
                      updated[idx] = { ...updated[idx], capacity: Number(e.target.value) };
                      saveDiningTables(updated);
                    }}
                  >
                    {[1, 2, 3, 4, 5, 6, 8, 10, 12, 16].map(seats => (
                      <option key={seats} value={seats}>
                        {seats} {seats === 1 ? 'paikka' : 'paikkaa'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.78rem' }}>📍 Salialue (Zone)</label>
                  <select
                    className="form-input"
                    value={tbl.zone || 'Pääsali'}
                    style={{ padding: '6px 10px', fontSize: '0.86rem' }}
                    onChange={e => {
                      const updated = [...diningTables];
                      updated[idx] = { ...updated[idx], zone: e.target.value };
                      saveDiningTables(updated);
                    }}
                  >
                    <option value="Pääsali">Pääsali</option>
                    <option value="Ikkunapöytä">Ikkunapöytä</option>
                    <option value="Terassi">Terassi</option>
                    <option value="VIP Loossi">VIP Loossi</option>
                    <option value="Baarialue">Baarialue</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Print Sheet Container (Visible when printing to PDF / A4) */}
      <div className="print-page" style={{ display: 'none' }}>
        {diningTables.map(tbl => (
          <div key={tbl.id} className="qr-standee-card">
            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{template.brand_title}</div>
            <div style={{ fontSize: '12px', color: '#555', marginBottom: '8px' }}>
              {template.tagline_fi}
            </div>
            {allQrUrls[tbl.table_number] && (
              <img
                src={allQrUrls[tbl.table_number]}
                alt={`Table ${tbl.table_number}`}
                style={{ width: '160px', height: '160px', margin: '0 auto' }}
              />
            )}
            <div
              style={{
                fontSize: '16px',
                fontWeight: 'bold',
                marginTop: '8px',
                border: '2px solid #000',
                padding: '4px 14px',
                display: 'inline-block'
              }}
            >
              PÖYTÄ {tbl.table_number} ({tbl.capacity || 4} PAIKKAA)
            </div>
            <div style={{ fontSize: '11px', marginTop: '6px', color: '#666' }}>
              theovenstory.fi
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
